import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Save } from 'lucide-react';

import Menu from '../components/menu';
import DashboardShell from '../../components/DashboardShell';
import { useMenuState } from '../../Common/MenuStateContext';
import SaListPageShell from '../../components/sa/SaListPageShell';
import ContentLoader from '../../components/ContentLoader';
import { Alert, Button, Checkbox, Select } from '../../components/ui';
import { getRoleList } from '../../services/roleApi';
import { getMenuList, getMenuPermissions } from '../../services/menuApi';
import {
  fetchRolePermissionsList,
  saveRoleMenuPermissions,
} from '../../services/rolePermissionsApi';
import { clearCacheOnAdminChange } from '../../Common/menuCacheUtils';
import { dashboardToast } from '../../utils/dashboardToast';
import {
  EMPTY_ROLE_RIGHTS_FORM,
  buildRolePermissionsPayload,
  groupMenusByRole,
  mapApiRolesToTableRows,
  type ApiRolePermissions,
  type MenuOption,
  type PermissionType,
  type PermissionsByMenuId,
  type RoleOption,
  type RoleRightsFormState,
  type RoleRightsRow,
} from '../../utils/rolePermissionsUtils';
import '../../assets/css/dashboard.css';

const ROLE_LIST_PAGE_SIZE = 500;

const clonePermissions = (permissions: PermissionsByMenuId): PermissionsByMenuId =>
  Object.fromEntries(
    Object.entries(permissions).map(([menuId, actions]) => [
      Number(menuId),
      { ...actions },
    ])
  );

const serializePermissions = (permissions: PermissionsByMenuId): string =>
  JSON.stringify(
    Object.keys(permissions)
      .map(Number)
      .sort((a, b) => a - b)
      .map((menuId) => {
        const actions = permissions[menuId] || {};
        const enabled = Object.keys(actions)
          .filter((code) => actions[code])
          .sort();
        return [menuId, enabled];
      })
      .filter(([, enabled]) => (enabled as string[]).length > 0)
  );

const RightsManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [, setSelectedMenuItem] = useState(null);

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]);
  const [apiRoleMenus, setApiRoleMenus] = useState<ApiRolePermissions[]>([]);
  const [permissionTypes, setPermissionTypes] = useState<PermissionType[]>([]);
  const [roleRightsRows, setRoleRightsRows] = useState<RoleRightsRow[]>([]);

  const [formState, setFormState] = useState<RoleRightsFormState>(EMPTY_ROLE_RIGHTS_FORM);
  const [baselineSerialized, setBaselineSerialized] = useState(() =>
    serializePermissions({})
  );
  const [isSaving, setIsSaving] = useState(false);

  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [isLoadingMenus, setIsLoadingMenus] = useState(true);
  const [menusError, setMenusError] = useState<string | null>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);

  const rolesLoadedRef = useRef(false);
  const menusLoadedRef = useRef(false);
  const permissionTypesLoadedRef = useRef(false);
  const rightsTableLoadedRef = useRef(false);

  const sortedMenus = useMemo(
    () => groupMenusByRole(apiRoleMenus, menuOptions, roleOptions),
    [apiRoleMenus, menuOptions, roleOptions]
  );
  const assignableMenuCount = useMemo(
    () =>
      sortedMenus.reduce((total, category) => {
        const sectionCount = (category.sections || []).reduce(
          (sum, section) => sum + section.items.length,
          0
        );
        return total + category.items.length + sectionCount;
      }, 0),
    [sortedMenus]
  );

  const loadRoleOptions = useCallback(async () => {
    setIsLoadingRoles(true);
    try {
      const { items } = await getRoleList(1, ROLE_LIST_PAGE_SIZE);
      setRoleOptions(items || []);
      setRolesError(null);
    } catch {
      setRolesError('Failed to load roles');
    } finally {
      setIsLoadingRoles(false);
    }
  }, []);

  const loadMenuOptions = useCallback(async () => {
    setIsLoadingMenus(true);
    try {
      const menus = await getMenuList();
      setMenuOptions(Array.isArray(menus) ? menus : []);
      setMenusError(null);
    } catch {
      setMenusError('Failed to load menu items');
      dashboardToast.error('Failed to load menu items');
    } finally {
      setIsLoadingMenus(false);
    }
  }, []);

  const loadPermissionTypes = useCallback(async () => {
    try {
      const types = await getMenuPermissions();
      setPermissionTypes(Array.isArray(types) ? types : []);
    } catch {
      dashboardToast.error('Failed to load permission types');
    }
  }, []);

  const loadRoleRightsTable = useCallback(async () => {
    setIsLoadingTable(true);
    try {
      const apiRoles = await fetchRolePermissionsList();
      setApiRoleMenus(apiRoles);
      setRoleRightsRows(
        mapApiRolesToTableRows(apiRoles, roleOptions, permissionTypes, menuOptions)
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      dashboardToast.error(`Failed to load rights: ${message}`);
    } finally {
      setIsLoadingTable(false);
    }
  }, [roleOptions, permissionTypes, menuOptions]);

  useEffect(() => {
    if (!rolesLoadedRef.current) {
      rolesLoadedRef.current = true;
      loadRoleOptions();
    }
  }, [loadRoleOptions]);

  useEffect(() => {
    if (!menusLoadedRef.current) {
      menusLoadedRef.current = true;
      loadMenuOptions();
    }
  }, [loadMenuOptions]);

  useEffect(() => {
    if (!permissionTypesLoadedRef.current) {
      permissionTypesLoadedRef.current = true;
      loadPermissionTypes();
    }
  }, [loadPermissionTypes]);

  useEffect(() => {
    const referenceDataReady =
      roleOptions.length > 0 && menuOptions.length > 0 && permissionTypes.length > 0;

    if (!rightsTableLoadedRef.current && referenceDataReady) {
      rightsTableLoadedRef.current = true;
      loadRoleRightsTable();
    }
  }, [roleOptions, menuOptions, permissionTypes, loadRoleRightsTable]);

  const applyRoleSelection = useCallback(
    (roleIdValue: string) => {
      if (!roleIdValue) {
        setFormState(EMPTY_ROLE_RIGHTS_FORM);
        setBaselineSerialized(serializePermissions({}));
        return;
      }

      const roleId = parseInt(roleIdValue, 10);
      const existing = roleRightsRows.find((row) => row.roleId === roleId);
      const permissions = existing ? clonePermissions(existing.permissions) : {};

      setFormState({
        editingRoleId: existing ? existing.roleId : null,
        roleId: roleIdValue,
        permissions,
      });
      setBaselineSerialized(serializePermissions(permissions));
    },
    [roleRightsRows]
  );

  const handleRoleSelect = useCallback(
    (value: string) => {
      applyRoleSelection(value);
    },
    [applyRoleSelection]
  );

  const toggleMenuPermission = useCallback((menuId: number, actionCode: string) => {
    setFormState((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [menuId]: {
          ...(prev.permissions[menuId] || {}),
          [actionCode]: !prev.permissions[menuId]?.[actionCode],
        },
      },
    }));
  }, []);

  const toggleCategoryPermission = useCallback(
    (menuIds: number[], actionCode: string, enabled: boolean) => {
      setFormState((prev) => {
        const nextPermissions = { ...prev.permissions };
        menuIds.forEach((menuId) => {
          nextPermissions[menuId] = {
            ...(nextPermissions[menuId] || {}),
            [actionCode]: enabled,
          };
        });
        return { ...prev, permissions: nextPermissions };
      });
    },
    []
  );

  const isDirty =
    Boolean(formState.roleId) &&
    serializePermissions(formState.permissions) !== baselineSerialized;

  const handleSaveRoleRights = useCallback(async () => {
    if (!formState.roleId) {
      dashboardToast.warning('Please select a role');
      return;
    }

    if (!isDirty) return;

    if (permissionTypes.length === 0) {
      dashboardToast.error('Permission types are not loaded yet');
      return;
    }

    const payload = buildRolePermissionsPayload(
      menuOptions,
      permissionTypes,
      formState.permissions
    );

    setIsSaving(true);
    try {
      const roleId = parseInt(formState.roleId, 10);
      const updatedApiRoles = await saveRoleMenuPermissions(roleId, payload);
      setApiRoleMenus(updatedApiRoles);
      const nextRows = mapApiRolesToTableRows(
        updatedApiRoles,
        roleOptions,
        permissionTypes,
        menuOptions
      );
      setRoleRightsRows(nextRows);

      const savedRow = nextRows.find((row) => row.roleId === roleId);
      const nextPermissions = savedRow ? clonePermissions(savedRow.permissions) : clonePermissions(formState.permissions);

      setFormState((prev) => ({
        ...prev,
        editingRoleId: roleId,
        permissions: nextPermissions,
      }));
      setBaselineSerialized(serializePermissions(nextPermissions));
      clearCacheOnAdminChange(roleId);
      dashboardToast.success('Rights saved successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      dashboardToast.error(`Failed to save rights: ${message}`);
    } finally {
      setIsSaving(false);
    }
  }, [formState, isDirty, menuOptions, permissionTypes, roleOptions]);

  const isPageLoading = isLoadingRoles || isLoadingMenus || isLoadingTable;
  const pageError = rolesError || menusError;
  const selectedRoleName =
    roleOptions.find((role) => String(role.id) === formState.roleId)?.roleName || '';

  const renderPermissionMatrix = (
    matrixId: string,
    matrixLabel: string,
    menus: typeof menuOptions
  ) => {
    const menuIds = menus.map((menu) => menu.id);

    return (
      <div className="rm-editor__matrix">
        <table className="rm-editor__table">
          <colgroup>
            <col className="rm-editor__col-menu" />
            {permissionTypes.map((permissionType) => (
              <col key={permissionType.id} className="rm-editor__col-action" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="rm-editor__matrix-menu">
                Permissions
              </th>
              {permissionTypes.map((permissionType) => {
                const actionCode = permissionType.permissionCode.toLowerCase();
                const allChecked =
                  menuIds.length > 0 &&
                  menuIds.every((menuId) =>
                    Boolean(formState.permissions[menuId]?.[actionCode])
                  );
                const inputId = `rm-cat-${matrixId}-${actionCode}`;

                return (
                  <th
                    key={permissionType.id}
                    scope="col"
                    className="rm-editor__matrix-action"
                  >
                    <span className="rm-editor__matrix-action-label">
                      {permissionType.permissionName}
                    </span>
                    <Checkbox
                      id={inputId}
                      checked={allChecked}
                      disabled={isSaving || menuIds.length === 0}
                      onChange={() =>
                        toggleCategoryPermission(menuIds, actionCode, !allChecked)
                      }
                      aria-label={`${matrixLabel}: toggle all ${permissionType.permissionName}`}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {menus.map((menu) => (
              <tr key={menu.id}>
                <th scope="row" className="rm-editor__matrix-menu">
                  {menu.name}
                </th>
                {permissionTypes.map((permissionType) => {
                  const actionCode = permissionType.permissionCode.toLowerCase();
                  const inputId = `rm-perm-${menu.id}-${actionCode}`;
                  const checked = Boolean(formState.permissions[menu.id]?.[actionCode]);

                  return (
                    <td key={permissionType.id} className="rm-editor__matrix-action">
                      <Checkbox
                        id={inputId}
                        checked={checked}
                        disabled={isSaving}
                        onChange={() => toggleMenuPermission(menu.id, actionCode)}
                        aria-label={`${menu.name}: ${permissionType.permissionName}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={setSelectedMenuItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Rights Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Role Rights</h5>
              <p className="dash-list-toolbar__desc">
                Select a role, assign menu access rights, then save your changes.
              </p>
            </div>
          </div>

          {isPageLoading ? (
            <ContentLoader label="Loading rights…" />
          ) : pageError ? (
            <Alert variant="error">{pageError}</Alert>
          ) : (
            <section className="rm-editor" aria-label="Role rights editor">
              <header className="rm-editor__header">
                <div className="rm-editor__header-copy">
                  <h2 className="rm-editor__header-title">Role rights</h2>
                  <p className="rm-editor__header-desc">
                    {selectedRoleName
                      ? `Editing permissions for ${selectedRoleName}`
                      : 'Choose a role to assign menu permissions'}
                  </p>
                </div>
                <Button
                  type="button"
                  className="rm-editor__save"
                  onClick={handleSaveRoleRights}
                  isLoading={isSaving}
                  disabled={isSaving || !isDirty}
                >
                  <Save size={16} aria-hidden />
                  Save
                </Button>
              </header>

              <div className="rm-editor__body">
                <div className="rm-editor__row rm-editor__row--select">
                  <label className="rm-editor__label" htmlFor="rm-role-select">
                    Select role
                  </label>
                  <div className="rm-editor__control">
                    <Select
                      id="rm-role-select"
                      value={formState.roleId}
                      onChange={handleRoleSelect}
                      disabled={isSaving}
                      placeholder="Select role…"
                      aria-label="Select role"
                      options={[
                        { value: '', label: 'Select role…' },
                        ...roleOptions.map((role) => ({
                          value: String(role.id),
                          label: role.roleName,
                        })),
                      ]}
                    />
                  </div>
                </div>

                {!formState.roleId ? (
                  <p className="rm-editor__empty">
                    Select a role above to view and edit its rights.
                  </p>
                ) : assignableMenuCount === 0 ? (
                  <p className="rm-editor__empty">No menu items available.</p>
                ) : permissionTypes.length === 0 ? (
                  <p className="rm-editor__empty">No permission types available.</p>
                ) : (
                  <div className="rm-editor__categories">
                    {sortedMenus.map((category) => {
                      const sections = category.sections || [];
                      const hasSections = sections.length > 0;
                      const sizeClass =
                        category.size === 'large'
                          ? ' rm-editor__category--large'
                          : category.size === 'compact'
                            ? ' rm-editor__category--compact'
                            : '';

                      return (
                        <section
                          key={category.id}
                          className={`rm-editor__category${sizeClass}`}
                          aria-label={`${category.name} permissions`}
                        >
                          <header className="rm-editor__category-head">
                            <div className="rm-editor__category-copy">
                              <h3 className="rm-editor__category-title">{category.name}</h3>
                              <p className="rm-editor__category-desc">
                                {hasSections
                                  ? 'SuperAdmin and Admin menus grouped together for easier review.'
                                  : `Menus and pages available to the ${category.name} role.`}
                              </p>
                            </div>
                          </header>

                          {hasSections
                            ? sections.map((section) => {
                                const sectionSizeClass =
                                  section.size === 'large'
                                    ? ' rm-editor__section--large'
                                    : section.size === 'compact'
                                      ? ' rm-editor__section--compact'
                                      : '';

                                return (
                                  <div
                                    key={section.id}
                                    className={`rm-editor__section${sectionSizeClass}`}
                                  >
                                    <div className="rm-editor__section-head">
                                      <h4 className="rm-editor__section-title">
                                        {section.name}
                                      </h4>
                                      <p className="rm-editor__section-desc">
                                        {section.size === 'large'
                                          ? 'Primary admin platform menus, including SuperAdmin Dashboard.'
                                          : 'Compact Admin role menus.'}
                                      </p>
                                    </div>
                                    {renderPermissionMatrix(
                                      section.id,
                                      section.name,
                                      section.items
                                    )}
                                  </div>
                                );
                              })
                            : renderPermissionMatrix(
                                category.id,
                                category.name,
                                category.items
                              )}
                        </section>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </SaListPageShell>
    </DashboardShell>
  );
};

export default RightsManagement;
